import CommonFormControl from "@/components/utils/CommonFormControl";
import { CommonStyledSelect, CommonStyledSelectMenuProps } from "@/components/utils/SelectStyles";
import CountriesService, { CountryData, StateData } from "@/services/countries/CountriesService";
import { FormControlProps, FormLabel, SelectChangeEvent, Skeleton } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Control, Controller, ControllerRenderProps, FieldValues, UseFormRegister, useController } from "react-hook-form";
import { getStatesByCountry } from "./Countries/CountriesStaticStatesDataUtils";

type StatesSelectProps = {
  register: UseFormRegister<FieldValues>;
  control: Control;
  country: CountryData;
  selectedState: StateData;
  setSelectedState: Dispatch<SetStateAction<StateData>>;
  formControlProps?: FormControlProps;
  defaultValue?: string;
  title?: string;
  required?: boolean;
};

function StatesSelect(props: StatesSelectProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [states, setStates] = useState<StateData[]>([]);
  const [statesNames, setStatesNames] = useState<string[]>([]);
  const isInitialMount = useRef(true);

  const { field } = useController({
    name: "state",
    control: props.control,
    defaultValue: props.defaultValue || "",
  });

  useEffect(() => {
    if (!props.country) {
      setStates([]);
      setStatesNames([]);
      return;
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      field.onChange("");
      props.setSelectedState(null);
    }

    fetchAndSetStates(props.country);
  }, [props.country]);

  const fetchAndSetStates = (selectedCountry: CountryData) => {
    setLoading(true);
    if (selectedCountry) {
      CountriesService.getStatesList(selectedCountry?.iso2)
        .then((apiStates) => {
          let states = apiStates.getContentOrThrowError();
          if (!(states.length > 0)) {
            //SINCE THIS TWO ARE IMPORTANT AND MISSING IN THE API; WE ADDED MANUALLY
            if (selectedCountry.iso2 == "KY" || selectedCountry.iso2 == "VG") {
              states = getStatesByCountry(selectedCountry.iso2);
            }
          }
          setStates(states);
          setStatesNames(states.map((state) => state.name));
          if (props.defaultValue) {
            const defaultState = states.length === 0 ? null : states.find((s) => s.name === props.defaultValue) || null;
            props.setSelectedState(defaultState);
          }
        })
        .finally(() => setLoading(false));
    }
  };

  function handleChange(event: SelectChangeEvent<unknown>, field: ControllerRenderProps<FieldValues, "state">) {
    const selectedStateName = event.target.value;
    const selectedStateData = states.find((state) => state.name === selectedStateName);
    props.setSelectedState(selectedStateData);
    field.onChange(event);
  }

  return (
    <CommonFormControl {...props.formControlProps}>
      <FormLabel>{props.title ?? "State"}</FormLabel>
      <Controller
        name="state"
        control={props.control}
        defaultValue={props.defaultValue || ""}
        disabled={states.length === 0}
        render={({ field, fieldState: { error } }) =>
          loading ? (
            <Skeleton
              variant={"rectangular"}
              sx={{
                minHeight: "100%",
                borderRadius: "0.5rem",
                transform: "scale(1)",
              }}
            />
          ) : (
            <CommonStyledSelect
              {...props.register("state", { required: props.required })}
              value={field.value}
              error={!!error}
              MenuProps={CommonStyledSelectMenuProps}
              onChange={(event) => handleChange(event, field)}
            >
              {statesNames.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </CommonStyledSelect>
          )
        }
      />
    </CommonFormControl>
  );
}

export default StatesSelect;
